import { ref, computed } from 'vue';
import store from '@/store';

/**
 * useStockCompartido
 *
 * Encapsulates the read/write lifecycle of `stock_compartido` and `cupo_total`
 * for venue-less admin sessions.
 *
 * Hydration contract (REQ-SC-02):
 *   - Initial state is sourced from `back.events.sesiones.show`. No separate
 *     GET endpoint is consumed.
 *   - If the session resource does NOT expose `stock_compartido` or
 *     `cupo_total`, `dataUnavailable` becomes true and the view MUST NOT
 *     submit. `save()` short-circuits while that flag is set.
 *
 * Save contract (REQ-SC-03 / REQ-SC-04 / REQ-SC-05):
 *   - Always sends `{ stock_compartido, cupo_total }`. When the toggle is OFF
 *     and a previous cupo exists, it is re-sent to preserve the persisted
 *     value server-side.
 *   - `cupo_total` is required with min 0 whenever `stock_compartido` is ON.
 *     Local validation guards the API call; backend rules win otherwise.
 *   - The response payload (`res.data.data`) is the source of truth; refs
 *     are overwritten via `syncFromResponse`.
 *
 * Error contracts (REQ-SC-06 / REQ-SC-07 / REQ-SC-08 / REQ-SC-09):
 *   - 422 with `errors.cupo_total` → inline error on the input; refs untouched.
 *   - 422 with `errors.stock_compartido` → venue-backed banner; refs untouched.
 *   - 404 → toast; refs reset to safe defaults.
 *   - Network / 5xx → toast; refs untouched.
 *
 * @param {object} options
 * @param {string|number} options.eventoId
 * @param {string|number} options.sesionId
 * @param {object} options.config - Vue config instance with getVuexAlias()
 * @param {object} options.toast - Toast service (success/error)
 */
export function useStockCompartido({ eventoId, sesionId, config, toast }) {
    // ── PUBLIC REFS ──────────────────────────────────────────────────────────
    const stockCompartido = ref(false);
    const cupoTotal = ref(null);
    const entradasActivas = ref(null);
    const venueBackedDisabled = ref(false);
    const cupoTotalError = ref(null);
    const dataUnavailable = ref(false);
    const permissionDenied = ref(false);
    const loading = ref(false);

    // ── INTERNAL: last source-of-truth snapshot from the server ─────────────
    const persisted = ref({ stock_compartido: false, cupo_total: null });

    // ── COMPUTED ────────────────────────────────────────────────────────────

    /**
     * True when current refs diverge from the last persisted snapshot.
     * Used by the view to decide whether GUARDAR should be enabled.
     */
    const dirty = computed(() => {
        return Boolean(stockCompartido.value) !== Boolean(persisted.value.stock_compartido)
            || Number(cupoTotal.value) !== Number(persisted.value.cupo_total);
    });

    /**
     * REQ-SC-15 contract — True when toggle is ON AND there are active entradas.
     * Drives the deactivation warning.
     */
    const canDeactivateWarning = computed(() => {
        return Boolean(stockCompartido.value)
            && typeof entradasActivas.value === 'number'
            && entradasActivas.value > 0;
    });

    // ── HELPERS ─────────────────────────────────────────────────────────────

    function normalizeCupo(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    }

    /**
     * Replace internal state from a backend payload. Missing keys fall back to
     * safe defaults (REQ-SC-05 / UT-09) so we never silently fabricate or
     * preserve a stale value.
     */
    function syncFromResponse(data) {
        const payload = data && typeof data === 'object' ? data : {};

        if (Object.prototype.hasOwnProperty.call(payload, 'stock_compartido')) {
            persisted.value.stock_compartido = Boolean(payload.stock_compartido);
        } else {
            persisted.value.stock_compartido = false;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'cupo_total')) {
            persisted.value.cupo_total = normalizeCupo(payload.cupo_total);
        } else {
            persisted.value.cupo_total = null;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'entradas_activas')) {
            const raw = payload.entradas_activas;
            entradasActivas.value = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
        }

        // Overwrite visible refs from the server snapshot (REQ-SC-05).
        stockCompartido.value = persisted.value.stock_compartido;
        cupoTotal.value = persisted.value.cupo_total;
        cupoTotalError.value = null;
        permissionDenied.value = false;
    }

    /**
     * Roll the visible refs back to the last persisted snapshot.
     */
    function resetDraft() {
        stockCompartido.value = persisted.value.stock_compartido;
        cupoTotal.value = persisted.value.cupo_total;
        cupoTotalError.value = null;
        venueBackedDisabled.value = false;
    }

    function sessionResourceHasContractFields(payload) {
        return Boolean(payload)
            && typeof payload === 'object'
            && Object.prototype.hasOwnProperty.call(payload, 'stock_compartido')
            && Object.prototype.hasOwnProperty.call(payload, 'cupo_total');
    }

    function handleAxiosError(error) {
        const status = error && error.response ? error.response.status : null;
        const responseData = error && error.response ? error.response.data : null;

        if (status === 422) {
            const errors = (responseData && responseData.errors) || {};
            const cupoErrors = Array.isArray(errors.cupo_total) ? errors.cupo_total : [];
            const stockErrors = Array.isArray(errors.stock_compartido) ? errors.stock_compartido : [];

            if (cupoErrors.length > 0) {
                cupoTotalError.value = String(cupoErrors[0]);
            }
            if (stockErrors.length > 0) {
                venueBackedDisabled.value = true;
            }
            return;
        }

        if (status === 403) {
            // REQ-SC-10: explicit permissions error. Preserve the last persisted
            // snapshot — refs stay intact and the view can render the dedicated
            // banner driven by `permissionDenied`.
            permissionDenied.value = true;
            if (toast && typeof toast.error === 'function') {
                toast.error('No tienes permiso sobre este evento');
            }
            return;
        }

        if (status === 404) {
            if (toast && typeof toast.error === 'function') {
                toast.error('Sesión no encontrada');
            }
            // REQ-SC-08: reset to safe defaults.
            persisted.value = { stock_compartido: false, cupo_total: null };
            stockCompartido.value = false;
            cupoTotal.value = null;
            cupoTotalError.value = null;
            permissionDenied.value = false;
            return;
        }

        // REQ-SC-09: network / unknown failures fall back to a generic toast.
        if (toast && typeof toast.error === 'function') {
            toast.error('Error de red');
        }
    }

    // ── LIFECYCLE ───────────────────────────────────────────────────────────

    /**
     * Hydrate from the existing `back.events.sesiones.show` endpoint.
     * Sets `dataUnavailable` when the session resource lacks the contract
     * fields (REQ-SC-02 STOP condition).
     */
    async function load() {
        if (!eventoId || !sesionId) {
            return;
        }

        loading.value = true;
        dataUnavailable.value = false;
        venueBackedDisabled.value = false;
        permissionDenied.value = false;

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.events.sesiones.show'),
                {
                    filters: {},
                    evento_id: eventoId,
                    sesion_id: sesionId,
                }
            );

            const payload = res && res.data ? res.data.data : null;

            if (!sessionResourceHasContractFields(payload)) {
                dataUnavailable.value = true;
                if (toast && typeof toast.error === 'function') {
                    toast.error('La configuración de stock compartido no está disponible para esta sesión.');
                }
                return;
            }

            syncFromResponse(payload);
        } catch (error) {
            handleAxiosError(error);
        } finally {
            loading.value = false;
        }
    }

    /**
     * Commit the current draft to the backend.
     *
     * @returns {Promise<boolean>} true when a 200 was observed.
     */
    async function save() {
        // Hard guards — REQ-SC-02 STOP and REQ-SC-07 venue-backed.
        if (dataUnavailable.value || venueBackedDisabled.value) {
            return false;
        }
        // REQ-SC-10: if the user has no permission on this event/session,
        // refuse to submit. The view already disables the form controls on
        // this flag, but a programmatic call must also be a no-op.
        if (permissionDenied.value) {
            return false;
        }
        if (loading.value) {
            return false;
        }

        // Local validation (REQ-SC-03): when ON, cupo must be integer ≥ 0.
        if (stockCompartido.value) {
            const raw = cupoTotal.value;
            const numeric = Number(raw);
            if (raw === null || raw === undefined || raw === '' || !Number.isFinite(numeric)) {
                cupoTotalError.value = 'El cupo es obligatorio';
                return false;
            }
            if (!Number.isInteger(numeric) || numeric < 0) {
                cupoTotalError.value = 'El cupo debe ser un entero mayor o igual a 0';
                return false;
            }
        }

        cupoTotalError.value = null;
        venueBackedDisabled.value = false;

        // REQ-SC-04: always send both fields. When OFF, re-send the last known
        // cupo so the backend can preserve it.
        const normalizedCupo = normalizeCupo(cupoTotal.value);
        const payload = {
            evento_id: eventoId,
            sesion_id: sesionId,
            stock_compartido: Boolean(stockCompartido.value),
            cupo_total: normalizedCupo,
        };

        loading.value = true;

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.events.sesiones.stock-compartido.update'),
                payload
            );

            const data = res && res.data ? res.data.data : null;
            syncFromResponse(data || {});

            if (toast && typeof toast.success === 'function' && res && res.data && res.data.message) {
                toast.success(res.data.message);
            }
            return true;
        } catch (error) {
            handleAxiosError(error);
            return false;
        } finally {
            loading.value = false;
        }
    }

    return {
        // state refs
        stockCompartido,
        cupoTotal,
        entradasActivas,
        venueBackedDisabled,
        cupoTotalError,
        dataUnavailable,
        permissionDenied,
        loading,
        // computed
        dirty,
        canDeactivateWarning,
        // mutators / lifecycle
        syncFromResponse,
        resetDraft,
        load,
        save,
    };
}